import { StripeSubscriptionStatus } from "@cruisen/contract"
import { makeAutoObservable } from "mobx"
import { observer } from "mobx-react-lite"
import styles from "./styles.module.scss"
import SmallLogo from "../../../assets/smallLogo.png"
import ConfettiOne from "../../../assets/confettiOne.png"
import ConfettiTwo from "../../../assets/confettiTwo.png"
import { PlanDetails } from "./steps/PlanDetails"
import { match } from "ts-pattern"
import { CreditCardDetails } from "./steps/CreditCardDetails"
import { SubscriptionActivated } from "./steps/SubscriptionActivated"
import { useMemo } from "react"
import { getStripeConfig } from "../../../services/firebase-functions/billing"
import { useQuery } from "@tanstack/react-query"
import { getAuth } from "firebase/auth"
import Config from "../../../config"
import { useStores } from "../../../models/helpers/useStores.ts"
import { differenceInDays } from "date-fns"

class SubscriptionModalStore {
  step: number

  constructor() {
    this.step = 0

    makeAutoObservable(this)
  }

  setStep(step: number) {
    this.step = step
  }
}

export const SubscriptionModal = observer(({ onClose }: { onClose: () => void }) => {
  const rootStore = useStores()
  const store = useMemo(() => new SubscriptionModalStore(), [])

  const initialSubscriptionStatus = useMemo(
    () => rootStore.stripeSubscription?.status as StripeSubscriptionStatus,
    [],
  )
  const steps = useMemo(
    () =>
      !rootStore.stripeSubscription?.status || rootStore.stripeSubscription?.status === "trialing"
        ? rootStore.stripeSubscription?.isEarlyAdopter
          ? ["planDetails", "subscriptionActivated"]
          : ["planDetails", "creditCardDetails", "creditCardAdded"]
        : ["planDetails", "creditCardDetails", "subscriptionActivated"],
    [],
  )

  const currentStep = steps[store.step]
  const currentUser = getAuth().currentUser

  const { data: stripeConfig, isLoading } = useQuery({
    queryKey: ["stripeConfig", currentUser?.uid],
    queryFn: () => getStripeConfig(),
  })

  const stripeConfigPrice = stripeConfig?.prices[0]
  const discount = stripeConfigPrice?.coupons?.[0] || null
  const isDiscountApplied = discount !== null

  let trialPeriodDays: number
  if (rootStore.stripeSubscription?.id) {
    trialPeriodDays =
      rootStore.stripeSubscription?.trialStart && rootStore.stripeSubscription?.trialEnd
        ? differenceInDays(
            rootStore.stripeSubscription?.trialEnd,
            rootStore.stripeSubscription?.trialStart,
          )
        : 0
  } else {
    trialPeriodDays = discount?.metadata?.trialPeriodDays
      ? parseInt(discount.metadata.trialPeriodDays, 10)
      : Config.stripeDefaultTrialPeriodDays
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>
          <img src={SmallLogo} alt="Cruisen" />
        </div>
        <h2 className={styles.title}>
          {(!initialSubscriptionStatus || initialSubscriptionStatus === "trialing") &&
          rootStore.stripeSubscription?.isEarlyAdopter
            ? "Thank you!"
            : "Activate subscription!"}
        </h2>
        <img src={ConfettiOne} alt="Confetti" className={styles.confettiOne} />
        <img src={ConfettiTwo} alt="Confetti" className={styles.confettiTwo} />
      </header>
      <div className={styles.subscriptionDetails}>
        {match(currentStep)
          .with("planDetails", () => (
            <PlanDetails
              onSuccess={() => store.setStep(store.step + 1)}
              isEarlyAdopter={rootStore.stripeSubscription?.isEarlyAdopter}
              isDiscountApplied={isDiscountApplied}
              stripeConfigPrice={stripeConfigPrice}
              subscriptionStatus={rootStore.stripeSubscription?.status as StripeSubscriptionStatus}
              discount={discount}
              trialPeriodDays={trialPeriodDays}
              isStripeConfigLoading={isLoading}
            />
          ))
          .with("creditCardDetails", () => (
            <CreditCardDetails
              goBack={() => store.setStep(store.step - 1)}
              onSuccess={() => store.setStep(store.step + 1)}
              subscriptionStatus={rootStore.stripeSubscription?.status as StripeSubscriptionStatus}
              stripeConfigPrice={stripeConfigPrice}
            />
          ))
          .with("subscriptionActivated", "creditCardAdded", () => (
            <SubscriptionActivated
              onSuccess={() => onClose()}
              subscriptionHasPaymentMethod={!!rootStore.stripeSubscription?.defaultPaymentMethodId}
              subscriptionStatus={rootStore.stripeSubscription?.status as StripeSubscriptionStatus}
              trialPeriodDays={trialPeriodDays}
            />
          ))
          .otherwise(() => null)}
      </div>
    </div>
  )
})
